/**
 * GratitudeTab.jsx — read-only Registry ledger of daily gratitude entries.
 * Columns: Date · Team Member · Entry. Newest first, 25 per page. Entries are
 * immutable; only a founder sees a delete control (server also enforces this).
 */
import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatDate } from "@/lib/dateFormat";
import { Trash2, ChevronLeft, ChevronRight, Loader2, Heart, Download } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const fileUrl = (u) => (u && u.startsWith("/api/files/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

export default function GratitudeTab({ isFounder }) {
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null); // index into imageEntries being previewed

  const imageEntries = (data.items || []).filter((e) => e.image_url);
  const current = lightboxIdx != null ? imageEntries[lightboxIdx] : null;
  const showPrev = () => setLightboxIdx((i) => (i > 0 ? i - 1 : i));
  const showNext = () => setLightboxIdx((i) => (i < imageEntries.length - 1 ? i + 1 : i));

  const load = useCallback(() => {
    setLoading(true);
    api.get("/gratitude", { params: { page, limit: 25 } })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load gratitude entries"))
      .finally(() => setLoading(false));
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const resp = await api.get("/gratitude/pdf", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = "gratitude-ledger.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not generate PDF"); }
    finally { setDownloading(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this gratitude entry? This cannot be undone.")) return;
    try {
      await api.delete(`/gratitude/${id}`);
      toast.success("Entry deleted");
      if (data.items.length === 1 && page > 1) setPage((p) => p - 1);
      else load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-3" data-testid="gratitude-tab">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Heart size={13} className="text-[#F54800]" />
          Daily gratitude ledger — read-only{isFounder ? " (founder can remove entries)" : ""}. {data.total} total.
        </div>
        <button
          onClick={downloadPdf}
          disabled={downloading || data.total === 0}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-white bg-slate-900 hover:bg-slate-700 px-3 py-1.5 rounded-sm disabled:opacity-50"
          data-testid="gratitude-download-pdf"
        >
          <Download size={13} /> {downloading ? "Preparing…" : "Download PDF"}
        </button>
      </div>

      <div className="card-flat overflow-hidden" data-testid="gratitude-table">
        {loading ? (
          <div className="p-10 text-center text-xs text-slate-400 inline-flex items-center gap-2 w-full justify-center">
            <Loader2 size={12} className="animate-spin" /> Loading…
          </div>
        ) : data.items.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500">No gratitude entries yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-[11px] uppercase tracking-wide text-slate-500">
                <th className="px-4 py-2.5 font-medium w-32">Date</th>
                <th className="px-4 py-2.5 font-medium w-48">Team Member</th>
                <th className="px-4 py-2.5 font-medium">Entry</th>
                {isFounder && <th className="px-4 py-2.5 font-medium w-12" />}
              </tr>
            </thead>
            <tbody>
              {data.items.map((e) => (
                <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`gratitude-row-${e.id}`}>
                  <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">{formatDate(e.date)}</td>
                  <td className="px-4 py-2.5 text-slate-800 font-medium">{e.user_name}</td>
                  <td className="px-4 py-2.5 text-slate-700">
                    <div className="flex items-center gap-3">
                      {e.image_url && (
                        <button
                          type="button"
                          onClick={() => setLightboxIdx(imageEntries.findIndex((x) => x.id === e.id))}
                          title="View image"
                          data-testid={`gratitude-image-${e.id}`}
                        >
                          <img
                            src={fileUrl(e.image_url)}
                            alt="Gratitude attachment"
                            className="h-12 w-12 rounded-sm object-cover border border-slate-200 shrink-0 hover:opacity-80 cursor-zoom-in"
                          />
                        </button>
                      )}
                      <span>{e.entry_text}</span>
                    </div>
                  </td>
                  {isFounder && (
                    <td className="px-4 py-2.5 text-right">
                      <button
                        onClick={() => del(e.id)}
                        className="text-slate-300 hover:text-red-600"
                        title="Delete entry"
                        data-testid={`gratitude-delete-${e.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {data.pages > 1 && (
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span data-testid="gratitude-page-label">Page {data.page} of {data.pages}</span>
          <div className="flex items-center gap-1">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-slate-200 disabled:opacity-40 hover:bg-slate-50"
              data-testid="gratitude-prev"
            >
              <ChevronLeft size={13} /> Prev
            </button>
            <button
              disabled={page >= data.pages}
              onClick={() => setPage((p) => p + 1)}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-slate-200 disabled:opacity-40 hover:bg-slate-50"
              data-testid="gratitude-next"
            >
              Next <ChevronRight size={13} />
            </button>
          </div>
        </div>
      )}

      <Dialog open={current != null} onOpenChange={(o) => !o && setLightboxIdx(null)}>
        <DialogContent className="max-w-3xl p-2 bg-white" data-testid="gratitude-lightbox">
          <DialogTitle className="sr-only">Gratitude image{current ? ` — ${current.user_name}` : ""}</DialogTitle>
          {current && (
            <div className="relative">
              <img
                src={fileUrl(current.image_url)}
                alt={`Gratitude attachment from ${current.user_name}`}
                className="w-full h-auto max-h-[78vh] object-contain rounded-sm bg-slate-50"
                data-testid="gratitude-lightbox-image"
              />
              {imageEntries.length > 1 && (
                <>
                  <button
                    onClick={showPrev}
                    disabled={lightboxIdx === 0}
                    className="absolute left-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-black/55 text-white flex items-center justify-center hover:bg-black/75 disabled:opacity-30 disabled:cursor-not-allowed"
                    data-testid="gratitude-lightbox-prev"
                    aria-label="Previous image"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <button
                    onClick={showNext}
                    disabled={lightboxIdx === imageEntries.length - 1}
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-black/55 text-white flex items-center justify-center hover:bg-black/75 disabled:opacity-30 disabled:cursor-not-allowed"
                    data-testid="gratitude-lightbox-next"
                    aria-label="Next image"
                  >
                    <ChevronRight size={18} />
                  </button>
                </>
              )}
              <div className="flex items-center justify-between mt-1 px-1 text-xs text-slate-500">
                <span className="font-medium text-slate-700">{current.user_name} · {formatDate(current.date)}</span>
                {imageEntries.length > 1 && <span data-testid="gratitude-lightbox-counter">{lightboxIdx + 1} of {imageEntries.length}</span>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
